import { IManagementStore } from "./types";
import { observable, action, computed } from "mobx";
import RequestsProcessor, {AxiosCustomErrorProcessor} from "@src/Tools/AxiosRequestsProcessor";
import axios from "axios";
import Stores from "..";
import { Values } from "@src/Tools/Values";
import { Location, LocationShort } from "@src/Interfaces/Entity/Location";
import { Organisation } from "@src/Interfaces/Entity/Organisation";
import { Permission } from "@src/Interfaces/Entity/Permission";
import { ApplicationPermission } from "@src/Interfaces/Entity/ApplicationPermission";
import { PermissionGroup } from "@src/Interfaces/Entity/PermissionGroup";
import { Position } from "@src/Interfaces/Entity/Position";
import { Person, PersonShort, PersonProfileImage, AssociatedAccount } from "@src/Interfaces/Entity/Person";
import { Theme } from "@src/Interfaces/Entity/Theme";
import { ApplicationLink } from "@src/Interfaces/Entity/ApplicationLink";
import { Handover, HandoverCandidate } from "@src/Interfaces/Entity/Handover";
import { WebSocketSession } from "@src/Interfaces/Entity/WebSocketSession";
import { IOptions } from "@src/Interfaces/Entity/Options";
import { ISystemSettings } from "@src/Interfaces/Entity/SystemSettings";
import { AuditLog, AuditLogRecordsRequest } from "@src/Interfaces/Entity/AuditLog";
import { IEntities } from "@src/Stores/Search/types";
import { IQueryResult } from "@src/Interfaces/QueryResult";
import { HandoverExtended } from "@src/Shared/Handovers/Handover";
import { PositionTeam } from "@src/Interfaces/Entity/PositionTeam";
import { AuthProvider, ShortAuthProvider } from "@src/Interfaces/Entity/OAuthProvider";
import { IAuthApplication } from "@src/Interfaces/Entity/OAuthApplication";
import { SyncData } from "@src/Interfaces/Entity/SyncData";
import { IApiResponse } from "../../Interfaces/Entity/ApiResponse";
import {IEdgeDeploymentInstructionsData} from "@src/Interfaces/Entity/Deployment";
import { IUserApiTokenInfo } from "../../Interfaces/Entity/UserApiToken";
import { IUserApiTokenValue } from "../../Interfaces/Entity/UserApiTokenValue";

const requestsProcessor: RequestsProcessor = new RequestsProcessor();

interface ISelectItem {
  id: string;
  name: string;
  divider: boolean;
}

function sortByName(a: any, b: any) {
  if (a.name < b.name) {
    return -1;
  } else if (a.name > b.name) {
    return 1;
  } else {
    return 0;
  }
}

class ManagementStore implements IManagementStore {
  @observable activeOrganisations: Organisation[] = [];
  @observable organisations: Organisation[] = [];
  @observable activeLocations: Location[] = [];
  @observable locations: Location[] = [];
  @observable deletedLocations: Location[] = [];
  @observable handovers: HandoverExtended[] = [];
  @observable shortLocations: LocationShort[] = [];
  @observable activePermissions: Permission[] = [];
  @observable permissions: Permission[] = [];
  @observable applicationPermissions: ApplicationPermission[] = [];
  @observable activePermissionGroups: PermissionGroup[] = [];
  @observable permissionGroups: PermissionGroup[] = [];
  @observable activePositions: Position[] = [];
  @observable positions: Position[] = [];
  @observable activePositionTeams: PositionTeam[] = [];
  @observable positionTeams: PositionTeam[] = [];
  @observable activePersons: Person[] = [];
  @observable associatedAccounts: AssociatedAccount[] = []
  @observable persons: Person[] = [];
  @observable auditLogs: AuditLog[] = [];
  @observable auditLogsCount: number = 0;
  @observable needReviewAuditLogs: AuditLog[] = [];
  @observable shortPersons: PersonShort[] = [];
  @observable activeThemes: Theme[] = [];
  @observable themes: Theme[] = [];
  @observable baseApplicationLinks: ApplicationLink[] = [];
  @observable applicationLinks: ApplicationLink[] = [];
  @observable syncLogs: SyncData[] = [];

  @observable modalThemeType: number;
  @observable options: IOptions;
  @observable passwordOptions: IOptions;
  @observable oAuthProviders: AuthProvider[];
  @observable shortAuthProviders: ShortAuthProvider[];
  @observable shortRequiredAuthProviders: ShortAuthProvider;
  @observable activeOAuthProviders: AuthProvider[];
  @observable oAuthApplications: IAuthApplication[];
  @observable userApiTokens: IUserApiTokenInfo[];
  @observable systemSettings: ISystemSettings;

  @computed get shortLocationsWithAll(): ISelectItem[] {
    const allLocation: ISelectItem = {
      id: Stores.values.common.EmptyId,
      name: Values.getLocalizationValue("allLocations"),
      divider: true,
    };

    const locationsForSelect: ISelectItem[] = [allLocation].concat(
      Stores.management.shortLocations.map((loc: LocationShort) => ({
        id: loc.id,
        name: loc.name,
        divider: false,
      }))
    );

    return locationsForSelect;
  }

  @action.bound
  async getActiveOrganisations() {
    try {
      const response: IApiResponse<Organisation[]> = await requestsProcessor.get<IApiResponse<Organisation[]>>(
        "organisations?onlyActive=true",
        "GetActiveOrganisations"
      );
      this.activeOrganisations = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getOrganisations() {
    try {
      const response: IApiResponse<Organisation[]> = await requestsProcessor.get<IApiResponse<Organisation[]>>(
        "organisations",
        "GetOrganisations"
      );
      this.organisations = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async saveOrganisation(organisation: Organisation, isNew: boolean = false) {
    return await this.save(organisation, this.organisations, "organisations", "organisations", isNew);
  }

  @action.bound
  async removeOrganisation(id: string) {
    this.delete(id, this.organisations, "organisations");
  }

  @action.bound
  async getActiveLocations() {
    try {
      const response: IApiResponse<Location[]> = await requestsProcessor.get<IApiResponse<Location[]>>(
        "locations?onlyActive=true",
        "GetActiveLocations"
      );
      this.activeLocations = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getLocations() {
    try {
      const response: IApiResponse<Location[]> = await requestsProcessor.get<IApiResponse<Location[]>>("locations", "GetLocations");
      this.locations = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getDeletedLocations() {
    try {
      const response: IApiResponse<Location[]> = await requestsProcessor.get<IApiResponse<Location[]>>(
        "locations/deleted",
        "GetDeletedLocations"
      );
      this.deletedLocations = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getHandovers() {
    try {
      const response: HandoverExtended[] = await requestsProcessor.get<HandoverExtended[]>(
        "Handover/GetAll?onlyActive=true",
        "GetHandovers"
      );
      this.handovers = response;
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getAuditLogs(auditLogRecordsRequest?: AuditLogRecordsRequest) {
    try {
      const response: IQueryResult<AuditLog> = await requestsProcessor.post<IQueryResult<AuditLog>>(
        "AuditLog/GetRecords",
        "GetAuditLogsRecords",
        auditLogRecordsRequest
      );
      this.auditLogs = response.items;
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getNeedReviewAuditLogs() {
    try {
      const response: AuditLog[] = await requestsProcessor.get<AuditLog[]>(
        "AuditLog/GetNeedReviewRecords",
        "GetNeedReviewRecords"
      );
      return response as AuditLog[];
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async reviewAuditLogs(auditLogRecordIds: string[]) {
    try {
      const auditLogs = await requestsProcessor.post<AuditLog[]>(
        "AuditLog/ReviewRecords",
        "ReviewAuditLogRecords",
        auditLogRecordIds
      );
      return auditLogs;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getShortLocations() {
    try {
        const response: IApiResponse<LocationShort[]> = await requestsProcessor.get<IApiResponse<LocationShort[]>>(
        "locations/listGeneral",
        "GetShortLocations"
      );
      this.shortLocations = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async saveLocation(location: Location, isNew: boolean = false) {
    try {
      const response: IApiResponse<Location> = await requestsProcessor.post<IApiResponse<Location>>(
      `locations/saveLocation`,
      "SaveLocation",
      location
      );

      if (response) {
        if (isNew) {
          this.locations.push(response.data);
          this.locations = this.locations.slice().sort(sortByName);
        } else {
          const currentIndex: number = this.locations.findIndex((item: any) => item.id === response.data.id);
          this.locations[currentIndex] = response.data;
          Stores.search.updateFilteredEntity(response.data.id, "locations");
        }
      }
      return response.data;
  } catch (error) {
    console.error(error);
  }
  }

  @action.bound
  async removeLocation(location: Location) {
    await this.delete(location.id, this.locations, "locations");
    this.deletedLocations.push(location);
  }

  @action.bound
  async restoreDeletedLocation(id: string) {
    try {
      const response: IApiResponse<Location> = await requestsProcessor.post<IApiResponse<Location>>(
        `locations/${id}/restore`,
        "RestoreDeletedLocation"
      );
      this.locations.push(response.data);
      this.deletedLocations = this.deletedLocations.filter((location) => location.id !== response.data.id);
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getActivePermissions() {
    try {
      const response: IApiResponse<Permission[]> = await requestsProcessor.get<IApiResponse<Permission[]>>(
        "Permission?onlyActive=true",
        "GetActivePermissions"
      );
      this.activePermissions = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getPermissions() {
    try {
      const response: IApiResponse<Permission[]> = await requestsProcessor.get<IApiResponse<Permission[]>>("Permission", "GetPermissions");
      this.permissions = response.data;
        this.permissions.forEach((permission) => {
          permission.roles.forEach((role) => {
            role.displayTitle = `${role.applicationName} - ${role.name}`;
          });
      });

      return this.permissions;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async savePermission(permission: Permission, isNew: boolean = false) {
      var result = await this.save(permission, this.permissions, "Permission", "permissions", isNew);
      const currentIndex: number = this.permissions.findIndex((item: any) => item.id === result.id);
      this.permissions[currentIndex].roles.forEach((role) => role.displayTitle = `${role.applicationName} - ${role.name}`);

      return result;
  }

  @action.bound
  async removePermission(id: string) {
    this.delete(id, this.permissions, "Permission");
  }

  @action.bound
  async getApplicationPermissions() {
    try {
      const response: ApplicationPermission[] = await requestsProcessor.get<ApplicationPermission[]>(
        "ApplicationPermission/GetAll",
        "GetApplicationPermissions"
      );
      this.applicationPermissions = response;
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getActivePermissionGroups() {
    try {
        const response: IApiResponse<PermissionGroup[]> = await requestsProcessor.get < IApiResponse<PermissionGroup[]>>(
        "PermissionGroup?onlyActive=true",
        "GetActivePermissionGroups"
      );
      this.activePermissionGroups = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getPermissionGroups() {
    try {
      const response: IApiResponse<PermissionGroup[]> = await requestsProcessor.get<IApiResponse<PermissionGroup[]>>(
        "PermissionGroup",
        "GetPermissionGroups"
      );
      this.permissionGroups = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async savePermissionGroup(permissionGroup: PermissionGroup, isNew: boolean = false) {
    return await this.save(permissionGroup, this.permissionGroups, "PermissionGroup", "permissionGroups", isNew);
  }

  @action.bound
  async removePermissionGroup(id: string) {
    this.delete(id, this.permissionGroups, "PermissionGroup");
  }

  @action.bound
  async getActivePositions() {
    try {
      const response: IApiResponse<Position[]> = await requestsProcessor.get<IApiResponse<Position[]>>(
        "positions?onlyActive=true",
        "GetActivePositions"
      );
      this.activePositions = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getPositions() {
    try {
      const response: IApiResponse<Position[]> = await requestsProcessor.get<IApiResponse<Position[]>>("positions", "GetPositions");
      this.positions = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getActivePositionTeams() {
    try {
      const response: IApiResponse<PositionTeam[]> = await requestsProcessor.get<IApiResponse<PositionTeam[]>>(
        "positionTeams?onlyActive=true",
        "GetActivePositionTeams"
      );
      this.activePositionTeams = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getPositionTeams() {
    try {
      const response: IApiResponse<PositionTeam[]> = await requestsProcessor.get<IApiResponse<PositionTeam[]>>(
        "positionTeams",
        "GetPositionTeams"
      );
      this.positionTeams = response.data || [];
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async savePosition(position: Position, isNew: boolean = false) {
    return await this.save(position, this.positions, "positions", "positions", isNew);
  }

  @action.bound
  async savePositionTeam(positionTeam: PositionTeam, isNew: boolean = false) {
    return await this.save(positionTeam, this.positionTeams, "positionTeams", "positionTeams", isNew);
  }

  @action.bound
  async removePosition(id: string) {
    this.delete(id, this.positions, "positions");
  }

  @action.bound
  async removePositionTeam(id: string) {
    this.delete(id, this.positionTeams, "positionTeams");
  }

  @action.bound
  async getPerson(id: string) {
    try {
      const response = await requestsProcessor.get<IApiResponse<Person>>(`persons/${id}`, "GetPerson");
      return response.data;
    } catch (error) {
      console.error(error);
    }
    }

    @action.bound
    async getAssociatedAccounts(id: string) {
        try {
            const response: IApiResponse<AssociatedAccount[]> = await requestsProcessor.get<IApiResponse<AssociatedAccount[]>>(
                `persons/${id}/associatedAccounts`,
                "GetAssociatedAccounts"
            );
            this.associatedAccounts = response.data;
            return response.data;
        } catch (error) {
            console.error(error);
        }
    }

  @action.bound
  async getActivePersons() {
    try {
      const response: IApiResponse<Person[]> = await requestsProcessor.get<IApiResponse<Person[]>>(
        "persons?onlyActive=true",
        "GetActivePersons"
      );
      this.activePersons = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getPersons() {
    try {
      const response: IApiResponse<Person[]> = await requestsProcessor.get<IApiResponse<Person[]>>("persons", "GetPersons");
      this.persons = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getShortPersons() {
    try {
      const response: IApiResponse<PersonShort[]> = await requestsProcessor.get<IApiResponse<PersonShort[]>>(
        "persons/shortList",
        "GetShortPersons"
      );
      this.shortPersons = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async savePerson(person: Person, isNew: boolean = false) {
    return await this.save(person, this.persons, "persons", "persons", isNew);
  }

  @action.bound
  async removePerson(id: string) {
    this.delete(id, this.persons, "persons");
  }

  @action.bound
  async changePassword(id: string, password: string) {
    try {
      const result = await requestsProcessor.patch<any>("persons/password", "ChangePassword", {
        id: id,
        value: password,
      });
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async saveProfileImage(model: PersonProfileImage) {
    try {
      const result = await requestsProcessor.patch<any>("persons/image", "SaveProfileImage", model);
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async changePersonState(personId: string, state: number) {
    try {
      await requestsProcessor.patch<any>(
          `persons/${personId}/state`,
          "ChangePersonState",
          {
              state: state
          }
      );
      this.persons = this.persons.map((person) => {
        if (person.id !== personId) {
          return person;
        }
        person.state = state;
        return person;
      });
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async deactivateAllPersonSessions(personId: string) {
    try {
      await requestsProcessor.delete<any>(
        `persons/${personId}/sessions`,
        "DeactivateAllPersonSessions"
      );
      this.persons = this.persons.map((person) => {
        if (person.id !== personId) {
          return person;
        }
        person.lastActivity.hasActiveSessions = false;
        return person;
      });
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getOptions() {
    try {
      const response: IOptions = await requestsProcessor.get<IOptions>("Options/Get", "GetOptions");
      this.options = response;
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getPasswordOptions() {
    try {
      const response: IOptions = await requestsProcessor.get<IOptions>(
        "Options/GetPasswordOptions",
        "GetPasswordOptions"
      );
      this.passwordOptions = response;
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getOAuthProviders() {
    try {
      const response: IApiResponse<AuthProvider[]> = await requestsProcessor.get<IApiResponse<AuthProvider[]>>(
        "AuthProvider",
        "GetOAuthProviders"
      );
      this.oAuthProviders = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getActiveOAuthProviders() {
    try {
      const response: IApiResponse<AuthProvider[]> = await requestsProcessor.get<IApiResponse<AuthProvider[]>>(
        "AuthProvider?onlyActive=true",
        "GetActiveOAuthProviders"
      );
      this.activeOAuthProviders = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getShortAuthProviders(tenant?: string) {
    try {
      let requestUrl = "AuthProvider/GetShortList";
      if (tenant) {
        requestUrl += `?tenant=${tenant}`;
      }
      const response: ShortAuthProvider[] = await requestsProcessor.get<ShortAuthProvider[]>(
        requestUrl,
        "GetShortAuthProviders"
      );
      this.shortAuthProviders = response;
      this.shortRequiredAuthProviders = this.shortAuthProviders.find((provider: ShortAuthProvider) => provider.isRequired == true);
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getOAuthApplications() {
    try {
      const response: IApiResponse<IAuthApplication[]> = await requestsProcessor.get<IApiResponse<IAuthApplication[]>>(
        "AuthApplication",
        "GetOAuthApplications"
      );
      this.oAuthApplications = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getOAuthApiTokens() {
    try {
      const response: IApiResponse<IUserApiTokenInfo[]> = await requestsProcessor.get<IApiResponse<IUserApiTokenInfo[]>>("userTokens", "GetUserApiTokens");
      this.userApiTokens = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
    }

  @action.bound
  async getDescryptedUserApiToken(id: string) {
    try {
      const response: IApiResponse<IUserApiTokenValue> = await requestsProcessor.get<IApiResponse<IUserApiTokenValue>>(`userTokens/${id}/decrypted`, "GetDecryptedUserApiToken");
      const currentIndex: number = this.userApiTokens.findIndex((item: any) => item.id === id);
      this.userApiTokens[currentIndex].isReceived = true;

      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getSystemSettings() {
    try {
      const response: IApiResponse<ISystemSettings> = await requestsProcessor.get<IApiResponse<ISystemSettings>>(
        "settings",
        "GetSystemSettings"
        );
      this.systemSettings = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async saveOptions(options: IOptions) {
    try {
      const response: IOptions = await requestsProcessor.post<IOptions>("Options/Save", "SaveOptions", options);
      this.options = response;
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async saveOAuthProvider(oAuthProvider: AuthProvider, isNew: boolean = false) {
    try {
        const response: IApiResponse<AuthProvider> = await requestsProcessor.post<IApiResponse<AuthProvider>>(
        "AuthProvider",
        "SaveOAuthProvider",
        oAuthProvider
      );
      if (!response) {
        return null;
      }
      if (isNew) {
        this.oAuthProviders.push(response.data);
      } else {
        this.oAuthProviders = this.oAuthProviders.map((provider) => {
          if (provider.id !== response.data.id) {
            return provider;
          }
          return response.data;
        });
      }
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async saveOAuthApplication(oAuthApplication: IAuthApplication, isNew: boolean = false) {
    try {
      const response: IApiResponse<IAuthApplication> = await requestsProcessor.post<IApiResponse<IAuthApplication>>(
        "AuthApplication",
        "SaveOAuthApplication",
        oAuthApplication
      );
      if (!response) {
        return null;
      }
      if (isNew) {
        this.oAuthApplications.push(response.data);
      } else {
        this.oAuthApplications = this.oAuthApplications.map((application) => {
          if (application.id !== response.data.id) {
            return application;
          }
          return response.data;
        });
      }
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async saveSystemSettings(systemSettings: ISystemSettings) {
    try {
      const response: IApiResponse<ISystemSettings> = await requestsProcessor.post<IApiResponse<ISystemSettings>>(
        "settings",
        "SaveSystemSettings",
        systemSettings
      );
      this.systemSettings = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getActiveThemes() {
    try {
      const response: IApiResponse<Theme[]> = await requestsProcessor.get<IApiResponse<Theme[]>>("themes?onlyActive=true", "GetActiveThemes");
      this.activeThemes = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getThemes() {
    try {
      const response: IApiResponse<Theme[]> = await requestsProcessor.get<IApiResponse<Theme[]>>("themes", "GetThemes");
      this.themes = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getSyncLogs() {
    try {
      const response: SyncData[] = await requestsProcessor.get<SyncData[]>("Sync/GetLogs", "GetSyncLogs");
      this.syncLogs = response;
      return response;
    } catch (error) {
      console.error(error);
    }
  }
  
  @action.bound
  async getDeploymentInstructionsData() {
    try {
      const response: IApiResponse<IEdgeDeploymentInstructionsData> = await requestsProcessor.get(
        'deployment/instructions',
        'GetDeploymentInstructionsData'
      );
      
      return response.data;
    }
    catch (e) {
      console.error(e);
    }
  }
  
  @action.bound
  async downloadEnvironmentVariables(locationId: string) {
    try {
      const response: IApiResponse<File> = await requestsProcessor.get(
        `deployment/edge/${locationId}`,
        'GetEdgeEnvironmentDeploymentVariables'
      );
      
      if (!response) {
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'env';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error(e);
    }
  }
  
  @action.bound
  async clearLocationSecrets(locationId: string) {
    try {
      const response: IApiResponse<Location> = await requestsProcessor.delete(
        `deployment/edge/${locationId}`,
        'ClearLocationSecrets'
      );
      
      if (!response) {
        return null;
      }
      
      const currentIndex: number = this.locations.findIndex((item: any) => item.id === response.data.id);
      this.locations[currentIndex] = response.data;
      Stores.search.updateFilteredEntity(response.data.id, "locations");
      return response.data;
    } catch (e) {
      console.error(e);
    }
  }

  @action.bound
  async saveTheme(theme: Theme, isNew: boolean = false) {
    return await this.save(theme, this.themes, "themes", "themes", isNew);
  }

  @action.bound
  async removeTheme(id: string) {
    await this.delete(id, this.themes, "themes");
  }

  @action.bound
  async uploadFile(file: File, objectId: string, type: number = 0) {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(`Api/File/Upload?type=${type}&relatedObjectId=${objectId}`, formData);
    return response.data;
  }

  @action.bound
  async addAssociatedAccount(oauthProviderId: string, personId: string, subjectId: string) {
    try {
      await requestsProcessor.get<void>(
        `AuthProvider/AddAssociatedAccount?oauthProvider=${oauthProviderId}&personId=${personId}&subjectId=${subjectId}`,
        "AddAssociatedAccount"
      );
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async deleteAssociatedAccount(oauthProviderId: string, personId: string) {
    try {
      await requestsProcessor.get<void>(
        `AuthProvider/DeleteAssociatedAccount?oauthProvider=${oauthProviderId}&personId=${personId}`,
        "DeleteAssociatedAccount"
      );
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  setModalThemeType(type: number) {
    this.modalThemeType = type;
  }

  @action.bound
  async getBaseApplicationLinks(appId: number) {
    try {
      const response: ApplicationLink[] = await requestsProcessor.get<ApplicationLink[]>(
        `Environment/GetBaseApplicationLinks?applicationToSkip=${appId}`,
        "GetBaseApplicationLinks"
      );
      this.baseApplicationLinks = response;
      return response;
    } catch (error) {
      console.error(error);
    }
  }

    @action.bound
    async getApplicationLinks() {
    try {
        const response: IApiResponse<ApplicationLink[]> = await requestsProcessor.get<IApiResponse<ApplicationLink[]>>(
        "ApplicationLink",
        "GetApplicationLinks"
        );
        this.applicationLinks = response.data;
        return response.data;
    } catch (error) {
        console.error(error);
    }
  }

  @action.bound
  async saveApplicationLink(applicationLink: ApplicationLink, isNew: boolean = false) {
    const savedApp = await this.save(
      applicationLink,
      this.applicationLinks,
      "ApplicationLink",
      "applicationLinks",
      isNew
    );
    this.applicationLinks = this.applicationLinks.map((link) => {
      if (link.id !== savedApp.id) {
        return link;
      }
      return savedApp;
    });
    this.baseApplicationLinks = this.baseApplicationLinks.map((link) => {
      if (link.id !== savedApp.id) {
        return link;
      }
      return savedApp;
    });
    return savedApp;
  }

  @action.bound
  async removeApplicationLink(id: string) {
    this.delete(id, this.applicationLinks, "ApplicationLink");
  }

  @action.bound
  async removeOAuthProvider(id: string) {
    this.delete(id, this.oAuthProviders, "AuthProvider");
  }

  @action.bound
  async removeOAuthApplication(id: string) {
    this.delete(id, this.oAuthApplications, "AuthApplication");
  }

  @action.bound
  async removeUserApiToken(id: string) {
    this.delete(id, this.userApiTokens, "userTokens");
  }

  @action.bound
  async generateuUserApiToken(name: string) {
    try {
        const response: IApiResponse<IUserApiTokenInfo> = await requestsProcessor.get<IApiResponse<IUserApiTokenInfo>>(
        `userTokens/generate?name=${name}`,
        "GenerateUserApiToken"       
      );
      if (!response) {
        return null;
      }
      this.userApiTokens.push(response.data);
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getPendingHandoverIfAny() {
    try {
      const response: Handover = await requestsProcessor.post<Handover>(
        `Handover/GetPendingIfAny`,
        "GetPendingHandoverIfAny",
        {}
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getHandoverCandidates() {
    try {
      const response: HandoverCandidate[] = await requestsProcessor.post<HandoverCandidate[]>(
        `Handover/GetCandidates`,
        "getHandoverCandidates",
        {}
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getHandoverCandidatesByPosition(positionId: string, locationId: string) {
    try {
      const response: HandoverCandidate[] = await requestsProcessor.get<HandoverCandidate[]>(
        `Handover/GetCandidatesByPosition?positionId=${positionId}&locationId=${locationId}`,
        "getHandoverCandidatesByPosition"
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getHandoverMaySignOut() {
    try {
      const response: boolean = await requestsProcessor.post<boolean>(
        `Handover/MaySignOut`,
        "getHandoverMaySignOut",
        {}
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async doHandoverSignOut(note: string) {
    try {
      await requestsProcessor.post<any>(`Handover/SignOut`, "getHandoverSignOut", { note: note });
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async createHandover(handover: Handover) {
    try {
      const isSuccessRequest: boolean = await requestsProcessor.post<boolean>(
        `Handover/Create`,
        "createHandover",
        handover
      );
      return Boolean(isSuccessRequest);
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async withdrawHandover() {
    try {
      await requestsProcessor.post<any>(`Handover/Withdraw`, "withdrawHandover", {});
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async withdrawHandoverByPerson(handoverTo: string, locationId: string, positionId: string) {
    try {
      const isSuccessRequest: boolean = await requestsProcessor.get<any>(
        `Handover/WithdrawByPerson?handoverTo=${handoverTo}&locationId=${locationId}&positionId=${positionId}`,
        "withdrawHandover"
      );
      return Boolean(isSuccessRequest);
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async retriggerHandover() {
    try {
      await requestsProcessor.post<any>(`Handover/Retrigger`, "retriggerHandover", {});
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async syncApplication(locationId: string) {
    try {
      await requestsProcessor.get<any>(
        `Sync/SynchronizeApplications?locationId=${locationId}`,
        "SynchronizeApplications"
      );
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async restoreDatabaseBackup(backupName: string) {
    try {
      await requestsProcessor.get<any>(`Sync/RestoreDatabaseBackup?backupName=${backupName}`, "RestoreDatabaseBackup");
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getLocationForSync() {
    try {
      var res = await requestsProcessor.get<LocationShort>(
        `Sync/GetLocationForSync`,
        "GetLocationForSync"
        );
      this.shortLocations.push(res);
      return res;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async uploadDatabaseBackup(file: any) {
    try {
      await requestsProcessor.post<any>(`Sync/UploadDatabaseBackup`, "UploadDatabaseBackup", file);
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async downloadDatabaseBackup(backupName: string) {
    try {
      window.open(`/api/Sync/DownloadDatabaseBackup?backupFileName=${backupName}`, "_blank");
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async createDatabaseBackup() {
    try {
      const databaseBackupName = await requestsProcessor.get<string>(
        `Sync/CreateDatabaseBackup`,
        "CreateDatabaseBackup"
      );
      return databaseBackupName;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getAllBackupFileNames() {
    try {
      const databaseBackupFileNames = await requestsProcessor.get<string[]>(
        `Sync/GetAllBackupFileNames`,
        "GetAllBackupFileNames"
      );
      return databaseBackupFileNames;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async checkIsSynchronizationRunning() {
    try {
      const result: boolean = await requestsProcessor.get<boolean>(
        `Sync/CheckIsSynchronizationRunning`,
        "CheckIsSynchronizationRunning"
      );
      return result;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async checkIsSynchronizationRequired() {
    try {
      const result: boolean = await requestsProcessor.get<boolean>(
        `Sync/CheckIsSynchronizationRequired`,
        "CheckIsSynchronizationRequired"
      );
      return result;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async generatePasswordResetLink(personId: string) {
    try {
        const result: IApiResponse<Person> = await requestsProcessor.get<IApiResponse<Person>>(
        `persons/${personId}/password/resetLink`,
        "GeneratePasswordResetLink"
      );
      this.persons = this.persons.map((person) => {
        if (person.id !== personId) {
          return person;
        }
        return result.data;
      });
      return result.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async deletePasswordResetLink(personId: string) {
    try {
      const result: IApiResponse<Person> = await requestsProcessor.delete<IApiResponse<Person>>(
        `persons/${personId}/password/resetLink`,
        "DeletePasswordResetLink"
      );
      this.persons = this.persons.map((person) => {
        if (person.id !== personId) {
          return person;
        }
        return result.data;
      });
      return result.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async validateResetPasswordLink(resetLink: string) {
      try {
        const result: IApiResponse<boolean> = await requestsProcessor.post<IApiResponse<boolean>>(
          `persons/password/resetLink/validate`,
           "validateResetPasswordLink",
           {
               link: resetLink
           }
      );
      return result.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async validateCreatePasswordLink(createLink: string) {
      try {
        const result: IApiResponse<boolean> = await requestsProcessor.post<IApiResponse<boolean>>(
         `persons/password/createLink/validate`,
          "validateCreatePasswordLink",
          {
              link: createLink
          }
        );
        return result.data;
      } catch (error) {
        console.error(error);
      }
  }

  @action.bound
  async resetPassword(resetLink: string, password: string) {
    try {
        await requestsProcessor.post<boolean>("persons/password/reset",
            "ResetPassword",
            {
                link: resetLink,
                password: password,
            });
    } catch (error) {
      console.error(error);
    }
  }
  @action.bound
  async createPassword(createtLink: string, password: string) {
    try {
        await requestsProcessor.post<boolean>("persons/password/create",
            "CreatePassword",
            {
                link: createtLink,
                password: password,
            });
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async sentResetLinkToUserMail(username: string, email: string) {
    try {
        const result = await requestsProcessor.post<IApiResponse<boolean>>(
        `persons/password/resetLink/send`,
        "SentResetLinkToUserMail",
          {
              email: email,
              username: username
          }
      );
      return result.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getWebSocketSessions() {
    try {
      const response: WebSocketSession[] = await requestsProcessor.get<WebSocketSession[]>(
        "Environment/GetWebSocketSessions",
        "GetWebSocketSessions"
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async save<T>(data: T, collections: T[], query: string, filterName: keyof IEntities, isNew: boolean) {
      try {
      const response: IApiResponse<T> = await requestsProcessor.post<IApiResponse<T>>(query, `Save${query.split("/")[0]}`, data);
      if (response) {
        if (isNew) {
          collections.push(response.data);
          collections = collections.slice().sort(sortByName);
        } else {
          const currentIndex: number = collections.findIndex((item: any) => item.id === response.data.id);
          collections[currentIndex] = response.data;
          Stores.search.updateFilteredEntity(response.data.id, filterName);
        }
      }
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  async delete<T>(id: string, collections: T[], query: string) {
    try {
      await requestsProcessor.delete<T>(query+`/${id}`, `Delete${query.split("/")[0]}`);
      const currentIndex: number = collections.findIndex((item: any) => item.id === id);
      collections.splice(currentIndex, 1);
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async createPerson(person: Person, onError: AxiosCustomErrorProcessor) {
    return (await requestsProcessor.post<IApiResponse<Person>>(
        `persons`,
        "CreatePerson",
        person,
        false,
        onError
    )).data;
  }

  @action.bound
  async updatePerson(person: Person, onError: AxiosCustomErrorProcessor) {
      return (await requestsProcessor.patch<IApiResponse<Person>>(
        `persons/${person.id}`,
        "UpdatePerson",
        person,
        false,
          onError
      )).data;
  }
}

export default ManagementStore;
